import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import {
  Inbox, ChefHat, Utensils, CheckCircle2, XCircle, ShoppingBag, Users,
} from "lucide-react";
import { toast } from "sonner";

const SYMBOLS = { EUR: "€", USD: "$", TRY: "₺", AED: "AED" };

// Active pipeline (terminal states — paid + canceled — are excluded)
const PIPELINE = ["open", "preparing", "served", "paid"];
const TERMINAL_STATES = new Set(["paid", "canceled"]);

const STATUS_STYLE = {
  open: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40",
  preparing: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/40",
  served: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40",
  paid: "bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/40",
  canceled: "bg-zinc-500/15 text-zinc-500 border-zinc-500/40",
};

const STAGE_META = {
  open: { icon: Inbox, dotClass: "bg-amber-500" },
  preparing: { icon: ChefHat, dotClass: "bg-blue-500" },
  served: { icon: Utensils, dotClass: "bg-emerald-500" },
  paid: { icon: CheckCircle2, dotClass: "bg-purple-500" },
};

function timeAgo(iso, lang) {
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return lang === "de" ? "gerade eben" : lang === "tr" ? "az önce" : "just now";
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return lang === "de" ? `vor ${m} Min` : lang === "tr" ? `${m} dk önce` : `${m} min ago`;
  }
  const h = Math.floor(diff / 3600);
  return lang === "de" ? `vor ${h} Std` : lang === "tr" ? `${h} sa önce` : `${h} hr ago`;
}

/** Compact 4-stage progress strip. Current stage highlighted; past stages ticked. */
function StatusPipeline({ status, onJump, currency, disabled }) {
  const { t } = useLang();
  const activeIdx = PIPELINE.indexOf(status);
  const canceled = status === "canceled";
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="order-pipeline">
      {PIPELINE.map((stage, i) => {
        const meta = STAGE_META[stage];
        const Icon = meta.icon;
        const isPast = !canceled && activeIdx > i;
        const isCurrent = !canceled && activeIdx === i;
        const isFuture = canceled || activeIdx < i;
        return (
          <React.Fragment key={stage}>
            <button
              type="button"
              disabled={disabled || isCurrent || canceled}
              onClick={() => !isCurrent && !canceled && onJump && onJump(stage)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold transition-all ${
                isCurrent
                  ? "bg-purple-600 text-white shadow-md scale-105"
                  : isPast
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                    : "bg-secondary text-muted-foreground hover:bg-secondary/80"
              } ${isFuture && !canceled ? "cursor-pointer" : ""}`}
              data-testid={`pipeline-${stage}`}
              title={t(`orders.${stage}`)}
            >
              <Icon className="h-3 w-3" />
              <span>{t(`orders.${stage}`)}</span>
            </button>
            {i < PIPELINE.length - 1 && (
              <div className={`h-0.5 w-3 sm:w-6 rounded-full ${isPast ? "bg-emerald-500/60" : "bg-secondary"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function Orders() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("open");
  const lastOpenCountRef = useRef(0);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get(`/restaurants/${id}/orders`, { params });
      if (filter === "open" && data.length > lastOpenCountRef.current && lastOpenCountRef.current > 0) {
        try { audioRef.current?.play().catch(() => {}); } catch { /* noop */ }
      }
      lastOpenCountRef.current = filter === "open" ? data.length : lastOpenCountRef.current;
      setOrders(data);
    } catch { /* ignore transient errors */ }
  };

  useEffect(() => {
    setOrders([]);           // clear stale state so old-filter cards don't flash
    lastOpenCountRef.current = 0;
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [id, filter]);

  const setStatus = async (order, next) => {
    if (order.status === next) return;
    // Optimistic UI: update local state immediately so admin sees the change
    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: next } : o)));
    try {
      await api.patch(`/orders/${order.id}`, { status: next });
      toast.success(`${t("cart.tableNumber")} ${order.table_label || "?"} → ${t(`orders.${next}`)}`);
      load();
    } catch (e) {
      // Rollback local state on failure
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: order.status } : o)));
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  // Group active orders by table for readability. Terminal statuses shown flat.
  const groups = useMemo(() => {
    if (filter === "paid" || filter === "canceled") {
      // flat rendering — no per-table grouping needed for archived
      return [{ table: null, orders, subtotal: 0 }];
    }
    const map = new Map();
    for (const o of orders) {
      const key = (o.table_label || o.table_id || "—") + "";
      if (!map.has(key)) map.set(key, { table: key, orders: [], subtotal: 0 });
      const g = map.get(key);
      g.orders.push(o);
      // Table subtotal excludes canceled orders
      if (o.status !== "canceled") g.subtotal += (o.total || 0);
    }
    return Array.from(map.values()).sort((a, b) => a.table.localeCompare(b.table));
  }, [orders, filter]);

  const renderOrderCard = (o) => {
    const symbol = SYMBOLS[o.currency] || "€";
    const isCanceled = o.status === "canceled";
    return (
      <div key={o.id} className="rounded-2xl border bg-card p-4 space-y-3 hover:shadow-md transition-shadow" data-testid={`order-card-${o.id}`}>
        {/* Header: status pill + time + total */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_STYLE[o.status]}`} data-testid={`order-status-${o.id}`}>
              {t(`orders.${o.status}`)}
            </span>
            <span className="text-[11px] opacity-60">{timeAgo(o.created_at, lang)}</span>
          </div>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400" data-testid={`order-total-${o.id}`}>
            {o.total.toFixed(2)}{symbol}
          </div>
        </div>

        {/* Items list */}
        <div className="space-y-1.5">
          {o.items.map((it, idx) => (
            <div key={idx} className="text-sm border-l-2 border-purple-500/30 pl-2.5">
              <div className="flex items-baseline justify-between gap-2">
                <div>
                  <span className="font-semibold">{it.qty}×</span> {it.name}
                  {it.variant && <span className="text-xs opacity-70"> · {it.variant}</span>}
                </div>
                <div className="text-xs opacity-70">{it.subtotal.toFixed(2)}{symbol}</div>
              </div>
              {it.extras?.length > 0 && (
                <div className="text-[11px] opacity-70 mt-0.5">+ {it.extras.map((e) => e.name).join(", ")}</div>
              )}
              {it.note && <div className="text-[11px] italic opacity-80 mt-0.5">💬 {it.note}</div>}
            </div>
          ))}
        </div>

        {o.note && (
          <div className="text-xs italic bg-secondary/60 rounded-xl px-3 py-2">💬 {o.note}</div>
        )}

        {/* Bottom: full status pipeline (primary interaction) + separate red cancel */}
        <div className="pt-2 border-t border-current/10 space-y-3">
          <StatusPipeline
            status={o.status}
            currency={o.currency}
            disabled={isCanceled}
            onJump={(stage) => setStatus(o, stage)}
          />
          {!isCanceled && o.status !== "paid" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setStatus(o, "canceled")}
              className="w-full rounded-full text-destructive hover:bg-destructive/10 border border-destructive/30"
              data-testid={`order-action-${o.id}-canceled`}
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />{t("orders.cancel")}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <audio ref={audioRef} src="data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQwAADB8AhH0AAgAgAAA/w==" preload="auto" />

      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-purple-500/15 text-purple-600 dark:text-purple-400 grid place-items-center">
              <ShoppingBag className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold" data-testid="orders-title">{t("orders.title")}</h1>
              <p className="text-xs opacity-70">{orders.length} {t("orders.items")}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 overflow-x-auto scrollbar-hide" data-testid="orders-filter-tabs">
          {["open", "preparing", "served", "paid", "canceled", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                filter === s ? "bg-purple-600 text-white border-purple-600" : "bg-background hover:border-purple-500/50"
              }`}
              data-testid={`orders-filter-${s}`}
            >
              {t(s === "all" ? "orders.all" : `orders.${s}`)}
            </button>
          ))}
        </div>

        {orders.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-16 text-center" data-testid="orders-empty">
            <Inbox className="h-8 w-8 mx-auto opacity-40" />
            <div className="mt-3 text-sm opacity-70">{t("orders.empty")}</div>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <section key={g.table || "flat"} data-testid={g.table ? `table-group-${g.table}` : "orders-flat"}>
                {g.table && (
                  <div className="flex items-baseline justify-between mb-2 px-1">
                    <div className="flex items-center gap-2 font-bold">
                      <Users className="h-4 w-4 opacity-70" />
                      <span>{t("orders.table")} <span className="text-purple-600 dark:text-purple-400">{g.table}</span></span>
                      <span className="text-xs font-normal opacity-60">· {g.orders.length} {t("orders.items")}</span>
                    </div>
                    <div className="text-sm font-bold" data-testid={`table-subtotal-${g.table}`}>
                      {t("orders.openBill")}: <span className="text-purple-600 dark:text-purple-400">{g.subtotal.toFixed(2)}{SYMBOLS[g.orders[0]?.currency] || "€"}</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {g.orders.map(renderOrderCard)}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
